// In-perspective track-card selector (story #850): the same card chrome selects a
// `Finding.category` track — Datan laatu, Analyysi's Talotekniikka/Vyöhykkeet, Optimointi's
// Mahdollisuus — at both portfolio and building scope. Clicking a card filters that perspective's
// `FindingsList` to the card's category (the caller owns the selection state).
import type { LucideIcon } from "lucide-react";
import { moduleCard } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { cn } from "@/lib/utils";

export interface TrackSelectorCardProps {
  icon: LucideIcon;
  label: string;
  detail: string;
  active: boolean;
  /** Omit for a lens with exactly one track (Datan laatu, Mahdollisuus) — renders a single
   * always-active, non-interactive card instead of a toggle. */
  onClick?: () => void;
  /** Optional KPI value (e.g. a pass-%) — omit for lenses with no computed health metric
   * (Datan laatu, Mahdollisuus render chrome-only). */
  value?: string;
  valueLoading?: boolean;
}

export function TrackSelectorCard({
  icon: Icon,
  label,
  detail,
  active,
  onClick,
  value,
  valueLoading = false,
}: TrackSelectorCardProps) {
  const interactive = onClick !== undefined;
  const Comp = interactive ? "button" : "div";
  const hasValue = value !== undefined;

  return (
    <Comp
      type={interactive ? "button" : undefined}
      role={interactive ? "tab" : undefined}
      aria-selected={interactive ? active : undefined}
      onClick={onClick}
      className={cn(
        moduleCard,
        "p-4 text-left transition-colors",
        interactive && "cursor-pointer",
        // Active/selected uses `primary` (the app's selection convention, per ConfigStatusTabs'
        // StatusTab) — not `accent`, which in the bkry palette reads too faint as a border/label.
        active
          ? "border-primary bg-primary/10 shadow-[0_0_0_1px_var(--primary)]"
          : "opacity-90 hover:opacity-100",
      )}
    >
      <CardGlowOverlay />
      {active && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-1 rounded-l-xl bg-primary"
        />
      )}
      <div className="relative flex items-center gap-2">
        <div
          className={cn(
            "h-8 w-8 shrink-0 rounded-md grid place-items-center border",
            active
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border/60 bg-background/60 text-muted-foreground",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-foreground truncate">
            {label}
          </div>
          <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
            {detail}
          </p>
        </div>
        {hasValue && (
          <div
            className={cn(
              "shrink-0 font-display text-lg tracking-tight tabular-nums",
              active ? "text-primary" : "text-foreground",
            )}
          >
            {valueLoading ? "…" : value}
          </div>
        )}
      </div>
    </Comp>
  );
}
